import React, { useState, useEffect } from 'react';
import { FiDownload, FiX, FiSmartphone } from 'react-icons/fi';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner after 3 seconds if not dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Check if user previously dismissed but it's been a week
    const dismissedTime = localStorage.getItem('pwa-install-dismissed-time');
    if (dismissedTime) {
      const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (parseInt(dismissedTime) < weekAgo) {
        localStorage.removeItem('pwa-install-dismissed');
        localStorage.removeItem('pwa-install-dismissed-time');
      }
    }

    // Show iOS instructions if on iOS and not installed
    if (iOS && !standalone) {
      const iosDismissed = localStorage.getItem('pwa-ios-dismissed');
      if (!iosDismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ PWA installed');
    }
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
    if (isIOS) {
      localStorage.setItem('pwa-ios-dismissed', 'true');
    }
  };

  // Don't show if already installed
  if (isStandalone) return null;

  // Don't show banner if user dismissed
  if (!showInstallBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slideUp">
      <div className="max-w-lg mx-auto bg-gradient-to-r from-[#DC143C] to-[#B91C3C] rounded-2xl shadow-2xl p-4 text-white">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <FiSmartphone className="text-2xl" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-bold text-lg">Instalar BarueriFlash</h3>
            {isIOS ? (
              <p className="text-sm text-white/80 mt-1">
                Toque em <span className="inline-flex items-center bg-white/20 px-1.5 py-0.5 rounded text-xs">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L12 14M12 14L8 10M12 14L16 10M4 18H20"/>
                  </svg>
                </span> e depois em <strong>"Adicionar à Tela de Início"</strong>
              </p>
            ) : (
              <p className="text-sm text-white/80 mt-1">
                Adicione à sua tela inicial para acesso rápido e notificações de pedidos!
              </p>
            )}
          </div>

          <button 
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstallClick}
            className="w-full mt-3 py-3 bg-white text-[#DC143C] font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-white/90 transition-colors"
          >
            <FiDownload />
            Instalar Agora
          </button>
        )}

        {isIOS && (
          <div className="mt-3 bg-white/10 rounded-xl p-3">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">1</div>
              <span>Toque no ícone de compartilhar</span>
            </div>
            <div className="flex items-center gap-3 text-sm mt-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">2</div>
              <span>Role e toque em "Adicionar à Tela de Início"</span>
            </div>
            <div className="flex items-center gap-3 text-sm mt-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">3</div>
              <span>Toque em "Adicionar"</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InstallPWA;
